import { useState } from 'react';
import { useClusterApi } from '../../api/ClusterApiProvider';
import { useAsync } from '../../api/useAsync';
import type { ResourceKind, Scope } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Button, Card, Loading } from '../../ui/primitives';
import { DataTable } from '../../ui/DataTable';
import { KIND_LABEL } from '../../ui/ResourceTable';
import { ErrorAlert } from '../../ui/Alert';
import { useToast } from '../../ui/Toast';
import { ScopeDrawer } from './ScopeDrawer';

/**
 * The boundaries this tenancy declares, and the form that declares one.
 *
 * A boundary is a standing permission: inside its prefix the named connector
 * writes catalogue rows by itself, each holding a lease. The rows are read with
 * the rest of the catalogue — they are the cluster's, not this console's — and
 * what is editable here is the boundary around them.
 */
export function ScopesCard({
  mayWrite,
  kinds,
}: {
  mayWrite: boolean;
  kinds: readonly ResourceKind[];
}) {
  const api = useClusterApi();
  const { t, plural } = useI18n();
  const toast = useToast();
  const state = useAsync<Scope[]>((signal) => api.listScopes(signal), 'cluster-scopes');
  const [declaring, setDeclaring] = useState(false);

  const remove = async (prefix: string) => {
    try {
      await api.deleteScope(prefix);
      toast(t('scopes.removed'));
      state.reload();
    } catch (error) {
      toast(error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <Card>
      <div className="card-head">
        <span className="ct">{t('scopes.title')}</span>
        {state.status === 'ready' && (
          <span className="ca faint">{plural('scopes.count', state.value.length)}</span>
        )}
        {mayWrite && (
          <Button
            size="sm"
            onClick={() => {
              setDeclaring(true);
            }}
          >
            {t('scopes.add')}
          </Button>
        )}
      </div>

      {state.status === 'loading' && <Loading />}
      {state.status === 'error' && <ErrorAlert error={state.error} />}
      {state.status === 'ready' && (
        <DataTable
          caption={t('scopes.title')}
          rows={state.value}
          rowKey={(scope) => scope.prefix ?? ''}
          empty={{ title: t('scopes.none'), body: t('scopes.none.body') }}
          noMatch={{ title: t('scopes.none'), body: t('scopes.none.body') }}
          columns={[
            {
              key: 'prefix',
              header: t('scopes.col.prefix'),
              mono: true,
              sort: (a, b) => (a.prefix ?? '').localeCompare(b.prefix ?? ''),
              cell: (scope) => (
                <span className="cell-flex across">
                  <span className="t-name mono">{scope.prefix}</span>
                  {scope.enabled === false && (
                    <span className="chip">{t('scopes.disabled')}</span>
                  )}
                </span>
              ),
            },
            {
              key: 'node',
              header: t('scopes.col.node'),
              mono: true,
              muted: true,
              cell: (scope) => scope.node,
            },
            {
              key: 'kinds',
              header: t('scopes.col.kinds'),
              hideSm: true,
              cell: (scope) => (
                <span className="cell-flex across">
                  {scope.kinds.map((kind) => (
                    <span key={kind} className="chip">
                      {t(KIND_LABEL[kind])}
                    </span>
                  ))}
                </span>
              ),
            },
            {
              key: 'environment',
              header: t('scopes.col.environment'),
              hideSm: true,
              cell: (scope) => scope.environment,
            },
            {
              // The count is the cluster's answer at the moment of the read; a
              // boundary the cluster could not be asked about shows the ceiling
              // alone rather than a zero nobody checked.
              key: 'rows',
              header: t('scopes.col.rows'),
              mono: true,
              align: 'end',
              sort: (a, b) => (a.rows ?? 0) - (b.rows ?? 0),
              cell: (scope) =>
                scope.rows === undefined
                  ? `—/${String(scope.max_live)}`
                  : `${String(scope.rows)}/${String(scope.max_live)}`,
            },
            {
              key: 'lease',
              header: t('scopes.col.lease'),
              mono: true,
              muted: true,
              hideSm: true,
              cell: (scope) => t('scopes.leaseSecs', { secs: scope.lease_secs }),
            },
            {
              key: 'remove',
              header: t('scopes.remove'),
              headerHidden: true,
              align: 'end',
              cell: (scope) =>
                mayWrite ? (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => {
                      void remove(scope.prefix ?? '');
                    }}
                  >
                    {t('scopes.remove')}
                  </Button>
                ) : null,
            },
          ]}
        />
      )}

      {declaring && (
        <ScopeDrawer
          kinds={kinds}
          onClose={() => {
            setDeclaring(false);
          }}
          onWritten={() => {
            setDeclaring(false);
            toast(t('scopes.declared'));
            state.reload();
          }}
        />
      )}
    </Card>
  );
}
